import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { BehaviorSubject, map, Observable, of, switchMap, catchError } from 'rxjs';
import {
  EventMessage, EventType, InteractionStatus,
  AuthenticationResult, AccountInfo, IdTokenClaims
} from '@azure/msal-browser';

export interface UiUser {
  displayName?: string;
  email?: string;
  oid?: string;       // object id
  upn?: string;
  roles?: string[];
  raw?: IdTokenClaims | null;

  photoUrl?: string;         // data:image/...;base64,...
  photoBase64?: string;
  photoContentType?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private active$ = new BehaviorSubject<AccountInfo | null>(null);

  // Escoge un tamaño pequeño (48x48 o 64x64 van perfect)
  private readonly photoSize = '48x48';
  private readonly graphBase = 'https://graph.microsoft.com/v1.0';
  private readonly graphScopes = ['User.Read'];
  // Nota: para /me/photo suele bastar con User.Read + permisos en el tenant,
  // pero para garantizar lectura de foto, usa ProfilePhoto.Read (según tu configuración de permisos).

  constructor(
    private msal: MsalService,
    private msalEvents: MsalBroadcastService,
    private http: HttpClient
  ) {
    // seed on startup
    const acc = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0] ?? null;
    if (acc) this.msal.instance.setActiveAccount(acc);
    this.active$.next(this.msal.instance.getActiveAccount());

    // keep in sync on login/logout/account changes
    this.msalEvents.msalSubject$
      .subscribe((msg: EventMessage) => {
        switch (msg.eventType) {
          case EventType.LOGIN_SUCCESS:
          case EventType.ACQUIRE_TOKEN_SUCCESS: {
            const auth = msg.payload as AuthenticationResult;
            if (auth.account) this.msal.instance.setActiveAccount(auth.account);
            this.active$.next(this.msal.instance.getActiveAccount());
            break;
          }
          case EventType.LOGOUT_SUCCESS:
            this.active$.next(null);
            break;
        }
      });

    // also refresh when interactions complete
    this.msalEvents.inProgress$
      .pipe(map((status) => status === InteractionStatus.None))
      .subscribe((ready) => {
        if (ready) this.active$.next(this.msal.instance.getActiveAccount());
      });
  }

  /** raw active account */
  get account$() {
    return this.active$.asObservable();
  }

  /** user with photo */
  get user$(): Observable<UiUser | null> {
    return this.account$.pipe(
      switchMap(acc => {
        const ui = this.mapToUiUser(acc);
        if (!ui || !acc) return of(null);

        return this.getMyPhotoDataUrl(acc).pipe(
          map(photoUrl => ({
            ...ui,
            photoUrl,
            // si quieres también base64 sin prefijo, puedes extraerlo:
            photoBase64: photoUrl?.includes('base64,') ? photoUrl.split('base64,')[1] : undefined,
            photoContentType: photoUrl?.startsWith('data:') ? photoUrl.substring(5, photoUrl.indexOf(';')) : undefined
          }))
        );
      })
    );
  }

  /** synchronous getter (sin foto, porque la foto es async) */
  get user(): UiUser | null {
    return this.mapToUiUser(this.msal.instance.getActiveAccount());
  }

  loginRedirect() {
    return this.msal.loginRedirect();
  }

  logoutRedirect() {
    return this.msal.logoutRedirect();
  }

  // --------------------------
  // Graph photo
  // --------------------------
  private getMyPhotoDataUrl(acc: AccountInfo): Observable<string | undefined> {
    // 1) token for Graph
    return of(null).pipe(
      switchMap(() => this.msal.instance.acquireTokenSilent({
        account: acc,
        scopes: this.graphScopes
      })),
      switchMap(result => {
        const token = result.accessToken;

        // 2) call Graph for small photo:
        // GET /me/photos/{size}/$value (binary)
        const url = `${this.graphBase}/me/photos/${this.photoSize}/$value`;

        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`
        });

        return this.http.get(url, { headers, responseType: 'blob' }).pipe(
          switchMap(blob => this.blobToDataUrl(blob)),
          catchError(err => {
            // 404 = no photo (normal)
            // cualquier otro error: también devolvemos undefined y pones placeholder en UI
            return of(undefined);
          })
        );
      }),
      catchError(() => of(undefined))
    );
  }

  private blobToDataUrl(blob: Blob): Observable<string> {
    return new Observable<string>((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(reader.result as string); // data:image/...;base64,...
        observer.complete();
      };
      reader.onerror = () => observer.error(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  // --------------------------
  // helpers
  // --------------------------
  private mapToUiUser(acc: AccountInfo | null): UiUser | null {
    if (!acc) return null;

    const claims = acc.idTokenClaims as any | undefined;

    const displayName =
      claims?.name ??
      ((claims?.given_name && claims?.family_name)
        ? `${claims.given_name} ${claims.family_name}`
        : undefined);

    const email =
      claims?.preferred_username ??
      (Array.isArray(claims?.emails) ? claims.emails[0] : undefined);

    const roles: string[] =
      Array.isArray(claims?.roles) ? claims.roles :
        claims?.role ? [claims.role] : [];

    return {
      displayName,
      email,
      oid: claims?.oid,
      upn: claims?.upn ?? claims?.preferred_username,
      roles,
      raw: claims ?? null
    };
  }
}







//import { Injectable } from '@angular/core';
//import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
//import { BehaviorSubject, map } from 'rxjs';
//import { EventMessage, EventType, InteractionStatus, AuthenticationResult, AccountInfo, IdTokenClaims } from '@azure/msal-browser';

//export interface UiUser {
//  displayName?: string;
//  email?: string;
//  oid?: string;       // object id
//  upn?: string;
//  roles?: string[];
//  raw?: IdTokenClaims | null;
//}

//@Injectable({ providedIn: 'root' })
//export class AuthService {
//  private active$ = new BehaviorSubject<AccountInfo | null>(null);

//  constructor(private msal: MsalService, private msalEvents: MsalBroadcastService) {
//    // seed on startup
//    const acc = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0] ?? null;
//    if (acc) this.msal.instance.setActiveAccount(acc);
//    this.active$.next(this.msal.instance.getActiveAccount());

//    // keep in sync on login/logout/account changes
//    this.msalEvents.msalSubject$
//      .subscribe((msg: EventMessage) => {
//        switch (msg.eventType) {
//          case EventType.LOGIN_SUCCESS:
//          case EventType.ACQUIRE_TOKEN_SUCCESS: {
//            const auth = msg.payload as AuthenticationResult;
//            if (auth.account) this.msal.instance.setActiveAccount(auth.account);
//            this.active$.next(this.msal.instance.getActiveAccount());
//            break;
//          }
//          case EventType.LOGOUT_SUCCESS:
//            this.active$.next(null);
//            break;
//        }
//      });

//    // also refresh when interactions complete
//    this.msalEvents.inProgress$
//      .pipe(map((status) => status === InteractionStatus.None))
//      .subscribe((ready) => {
//        if (ready) this.active$.next(this.msal.instance.getActiveAccount());
//      });
//  }

//  /** raw active account */
//  get account$() {
//    return this.active$.asObservable();
//  }

//  /** convenient, UI-ready projection */
//  get user$() {
//    return this.account$.pipe(map(acc => this.mapToUiUser(acc)));
//  }

//  /** synchronous getter (useful in guards/components) */
//  get user(): UiUser | null {
//    return this.mapToUiUser(this.msal.instance.getActiveAccount());
//  }

//  loginRedirect() {
//    return this.msal.loginRedirect();
//  }
//  logoutRedirect() {
//    return this.msal.logoutRedirect();
//  }

//  // === helpers ===
//  private mapToUiUser(acc: AccountInfo | null): UiUser | null {
//    if (!acc) return null;
//    const claims = acc.idTokenClaims as any | undefined;

//    const displayName =
//      claims?.name ??
//        (claims?.given_name && claims?.family_name) ? claims?.name ?? (`${claims.given_name} ${claims.family_name}`) : undefined;

//    const email =
//      claims?.preferred_username ??
//      (Array.isArray(claims?.emails) ? claims.emails[0] : undefined);

//    const roles: string[] =
//      Array.isArray(claims?.roles) ? claims.roles :
//        claims?.role ? [claims.role] : [];

//    return {
//      displayName,
//      email,
//      oid: claims?.oid,
//      upn: claims?.upn ?? claims?.preferred_username,
//      roles,
//      raw: claims ?? null
//    };
//  }
//}
